import {
  Flex,
  fromTheme,
  StrokeIcon,
  Tabs,
  Tooltip,
  usePlatformInfo,
  usePlatformProps,
} from '@acsbe/ui';
import { SetStateAction, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import AcsbLogo from '../../assets/logo.svg';
import {
  APPLICATION_MODES,
  useApplicationViewState,
  useDomainSelection,
  useMerchantSession,
} from '../../context';
import { Logger } from '../../utils/logger';
import { DashboardHeader } from '../dashboard/dashboard-header';
import { AccessibilityStatement } from './accessibility-statement';
import { AdvancedInstallation } from './customization/customization';
import { Subscription } from './subscription/subscription';

interface TabListProps {
  isWidgetEnabled: boolean;
  setIsWidgetEnabled: React.Dispatch<SetStateAction<boolean>>;
  planStatus: string;
  planName: string;
  updateCurrentTab: (tab: string) => void;
}

export const TabList = ({
  isWidgetEnabled,
  setIsWidgetEnabled,
  planStatus,
  planName,
  updateCurrentTab,
}: TabListProps) => {
  const TABS = {
    MANAGE_LICENSE: {
      tabId: 'manage-license',
      label: {
        mobile: 'Plan & payment',
        desktop: 'Plan & payment',
      },
    },
    ACCESSIBILITY_STATEMENT: {
      tabId: 'accessibility-statement',
      label: {
        mobile: 'Accessibility statement',
        desktop: 'Accessibility statement',
      },
    },
    CUSTOMIZE_WIDGET: {
      tabId: 'customize-widget',
      label: {
        mobile: 'Customize',
        desktop: 'Customize widget',
      },
    },
    DEFAULT_TABID: 'manage-license',
  };

  const p = usePlatformProps();
  const { isMobile, uptoPhablet } = usePlatformInfo();
  const [params, setParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(TABS.DEFAULT_TABID);
  const [forceUpdate, setForceUpdate] = useState(0);
  const {
    licenseId,
    accountId,
    licenseDetails,
    changedConfig,
    setShowDiscardModal,
  } = useDomainSelection();
  const { merchantDetails } = useMerchantSession();
  const { applicationMode } = useApplicationViewState();
  const redirectUrl = useMerchantSession().redirectUrl || document.referrer;

  const [accessibilityStatementEnabled, setAccessibilityStatementEnabled] =
    useState(false);
  const [customizeWidgetEnabled, setCustomizeWidgetEnabled] = useState(false);

  useEffect(() => {
    if (
      licenseDetails?.status === 'trial' ||
      licenseDetails?.status === 'active' ||
      licenseDetails?.status === 'grace' ||
      licenseDetails?.status === 'cancelled'
    ) {
      setAccessibilityStatementEnabled(true);
      setCustomizeWidgetEnabled(true);
    }
  }, [licenseDetails?.status]);

  useEffect(() => {
    const tab = params.get('tab');
    if (
      tab !== null &&
      (tab === TABS.MANAGE_LICENSE.tabId ||
        tab === TABS.CUSTOMIZE_WIDGET.tabId ||
        tab === TABS.ACCESSIBILITY_STATEMENT.tabId)
    ) {
      setActiveTab(tab);
      setParams((prev) => {
        prev.delete('tab');
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, setParams]);

  useEffect(() => {
    updateCurrentTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <Flex
      paddingInline={12.5}
      paddingBlockEnd={12.25}
      paddingBlockStart={5}
      {...p.uptoDesktop({ paddingInline: 8 })}
      {...p.uptoTablet({ paddingInline: 5 })}
      {...p.uptoDesktop({ paddingBlockEnd: 16.25 })}
      {...p.uptoTablet({ paddingBlockEnd: 23.5 })}
      {...p.uptoPhablet({ paddingBlockEnd: 7 })}
      {...p.uptoMobile({ paddingBlockEnd: 4.25 })}
      gap={4}
      stretch
    >
      <Flex>
        <Flex height="16px" marginBlockEnd={4}>
          <AcsbLogo />
        </Flex>
      </Flex>

      <Flex>
        <DashboardHeader />
      </Flex>

      <Flex stretch>
        {applicationMode === APPLICATION_MODES.LIMITED ? (
          <AdvancedInstallation />
        ) : (
          <StickyTabs
            key={`tabs-${forceUpdate}`}
            defaultValue={activeTab}
            onTabChanged={(newTab) => {
              if (
                newTab !== TABS.CUSTOMIZE_WIDGET.tabId &&
                changedConfig !== null
              ) {
                Logger.log(
                  'Unsaved changes present. Can preventing tab switch.',
                );
                setForceUpdate((prev) => prev + 1);
                setShowDiscardModal(true);
                return;
              }

              if (newTab === TABS.ACCESSIBILITY_STATEMENT.tabId) {
                // Force the component to re-render with the current active tab

                if (!accessibilityStatementEnabled) {
                  setForceUpdate((prev) => prev + 1);
                  return;
                }
              }

              if (newTab === TABS.CUSTOMIZE_WIDGET.tabId) {
                if (!customizeWidgetEnabled) {
                  // Force the component to re-render with the current active tab
                  setForceUpdate((prev) => prev + 1);
                  return;
                }
              }

              setActiveTab(newTab);
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }}
            label="Preview"
            tabs={[
              {
                value: TABS.MANAGE_LICENSE.tabId,
                label: isMobile
                  ? TABS.MANAGE_LICENSE.label.mobile
                  : TABS.MANAGE_LICENSE.label.desktop,
                icon: <StrokeIcon name="Briefcase" />,
                content: <Subscription isWidgetEnabled={isWidgetEnabled} />,
              },
              {
                value: TABS.ACCESSIBILITY_STATEMENT.tabId,
                label: (
                  <Tooltip
                    zIndex={10}
                    scale="invert"
                    content={
                      accessibilityStatementEnabled
                        ? 'Accessibility statement'
                        : 'Purchase a plan to view your site’s accessibility statement.'
                    }
                  >
                    <span
                      style={{
                        opacity: accessibilityStatementEnabled ? 1 : 0.5,
                        cursor: accessibilityStatementEnabled
                          ? 'pointer'
                          : 'not-allowed',
                      }}
                    >
                      {isMobile
                        ? TABS.ACCESSIBILITY_STATEMENT.label.mobile
                        : TABS.ACCESSIBILITY_STATEMENT.label.desktop}
                    </span>
                  </Tooltip>
                ),
                icon: <StrokeIcon name="Ribbon" />,
                content: <AccessibilityStatement licenseId={licenseId!} />,
              },
              {
                value: TABS.CUSTOMIZE_WIDGET.tabId,
                label: (
                  <Tooltip
                    zIndex={10}
                    scale="invert"
                    content={
                      customizeWidgetEnabled
                        ? 'Customize widget'
                        : 'Purchase a plan to customize your widget.'
                    }
                  >
                    <span
                      style={{
                        opacity: customizeWidgetEnabled ? 1 : 0.5,
                        cursor: customizeWidgetEnabled
                          ? 'pointer'
                          : 'not-allowed',
                      }}
                    >
                      {isMobile
                        ? TABS.CUSTOMIZE_WIDGET.label.mobile
                        : TABS.CUSTOMIZE_WIDGET.label.desktop}
                    </span>
                  </Tooltip>
                ),
                icon: <StrokeIcon name="Pencil" />,
                content: <AdvancedInstallation />,
              },
            ]}
          />
        )}
      </Flex>
    </Flex>
  );
};

const StickyTabs = styled(Tabs)`
  display: flex;
  flex: 1;
  [data-orientation='horizontal'] {
    &[role='tablist'] {
      position: sticky;
      z-index: 10;
      top: 0;
      background: ${fromTheme('palette.scales.base.background.static')};
    }
    &[role='tabpanel'] {
      &[data-state='active'] {
        display: flex !important;
        flex: 1;
      }
    }
  }
`;

const ZFlex = styled(Flex)``;

const AbsoluteFlex = styled(Flex)<{
  positionRight?: number;
  positionTop?: number;
  positionLeft?: number;
}>`
  right: ${(props) =>
    props.positionRight ? `${props.positionRight}px` : 'unset'};
  top: ${(props) => (props.positionTop ? `${props.positionTop}px` : 'unset')};
  left: ${(props) =>
    props.positionLeft ? `${props.positionLeft}px` : 'unset'};
`;
